import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Loader2, UserPlus, UserX, Search, Mail, Phone } from 'lucide-react';
import { searchUsers } from '@/lib/firebase/queries/organizations/searchUsers';
import { getOrganizationExaminers } from '@/lib/firebase/queries/organizations/examiners';
import { addExaminerToOrganization, removeExaminerFromOrganization } from '@/lib/firebase/queries/organizations/examiners';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Organization } from '@/types/organization';
import type { User } from '@/types';

interface ExaminerSelectorProps {
  organization: Organization | null;
  onSuccess?: () => void;
}

export function ExaminerSelector({ organization, onSuccess }: ExaminerSelectorProps) {
  const [availableExaminers, setAvailableExaminers] = useState<User[]>([]);
  const [currentExaminers, setCurrentExaminers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [availableSearchQuery, setAvailableSearchQuery] = useState('');
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const { currentUser } = useAuth();
  const { t, dir } = useLanguage();

  useEffect(() => {
    if (organization) {
      loadExaminers();
    }
  }, [organization]);

  const filterExaminers = (examiners: User[], query: string) => {
    if (!query.trim()) return examiners;
    
    const searchTerm = query.toLowerCase();
    return examiners.filter(examiner => {
      // Optional-chained: searchUsers casts a raw doc.data() spread to User, so a
      // user document with no email arrives as undefined and crashed the page.
      const nameEn = `${examiner.firstNameEn ?? ''} ${examiner.lastNameEn ?? ''}`.toLowerCase();
      const nameAr = `${examiner.firstNameAr ?? ''} ${examiner.lastNameAr ?? ''}`.toLowerCase();
      const email = examiner.email?.toLowerCase() || '';
      const phone = examiner.phone?.toLowerCase() || '';
      
      return nameEn.includes(searchTerm) || 
             nameAr.includes(searchTerm) || 
             email.includes(searchTerm) || 
             phone.includes(searchTerm);
    });
  };

  const filteredAvailableExaminers = filterExaminers(availableExaminers, availableSearchQuery);
  const filteredCurrentExaminers = filterExaminers(currentExaminers, currentSearchQuery);

  const loadExaminers = async () => {
    if (!organization) return;
    
    try {
      setLoading(true);
      const [available, current] = await Promise.all([
        searchUsers('examiner'),
        getOrganizationExaminers(organization.id)
      ]);

      // Filter out examiners that are already assigned
      const currentIds = new Set(current.map(e => e.id));
      setAvailableExaminers(available.filter(e => !currentIds.has(e.id)));
      setCurrentExaminers(current);
    } catch (error) {
      console.error('Error loading examiners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExaminer = async (examiner: User) => {
    if (!organization || !currentUser) return;

    try {
      setProcessingId(examiner.id);
      const success = await addExaminerToOrganization(
        organization.id,
        examiner.id,
        currentUser.uid
      );

      if (success) {
        setAvailableExaminers(prev => prev.filter(e => e.id !== examiner.id));
        setCurrentExaminers(prev => [...prev, examiner]);
        onSuccess?.();
      }
    } finally {
      setProcessingId(null);
    }
  };

  const handleRemoveExaminer = async (examiner: User) => {
    if (!organization) return;

    try {
      setProcessingId(examiner.id);
      const success = await removeExaminerFromOrganization(
        organization.id,
        examiner.id
      );

      if (success) {
        setCurrentExaminers(prev => prev.filter(e => e.id !== examiner.id));
        setAvailableExaminers(prev => [...prev, examiner]);
        onSuccess?.();
      }
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-6" dir={dir}>
      {/* Current Examiners */}
      <div className="space-y-4">
        <h3 className="font-medium">{t('dashboard.admin.organizations.members.currentExaminers')}</h3>
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={dir === 'rtl' ? 'البحث في الفاحصين الحاليين...' : 'Search current examiners...'}
            value={currentSearchQuery}
            onChange={(e) => setCurrentSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <ScrollArea className="h-[400px]">
          <div className="space-y-2 px-4 pb-4">
            {filteredCurrentExaminers.map((examiner) => (
              <div
                key={examiner.id}
                className="flex items-center gap-3 p-3 rounded-lg border"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    <span className="truncate max-w-[166px]">{examiner.email}</span>
                  </div>
                  {examiner.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      <span className="truncate max-w-[100px]">{examiner.phone}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">
                    {dir === 'rtl' 
                      ? `${examiner.firstNameAr} ${examiner.lastNameAr}`
                      : `${examiner.firstNameEn} ${examiner.lastNameEn}`
                    }
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveExaminer(examiner)}
                  disabled={!!processingId}
                  className="flex-shrink-0 bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700"
                >
                  {processingId === examiner.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UserX className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
            {filteredCurrentExaminers.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">
                {currentSearchQuery ? 
                  (dir === 'rtl' ? 'لا توجد نتائج للبحث' : 'No search results') :
                  t('dashboard.admin.organizations.members.noExaminersAssigned')
                }
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Available Examiners */}
      <div className="space-y-4">
        <h3 className="font-medium">{t('dashboard.admin.organizations.members.availableExaminers')}</h3>
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={dir === 'rtl' ? 'البحث في الفاحصين المتاحين...' : 'Search available examiners...'}
            value={availableSearchQuery}
            onChange={(e) => setAvailableSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <ScrollArea className="h-[400px]">
          <div className="space-y-2 px-4 pb-4">
            {filteredAvailableExaminers.map((examiner) => (
              <div
                key={examiner.id}
                className="flex items-center gap-3 p-3 rounded-lg border"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    <span className="truncate max-w-[166px]">{examiner.email}</span>
                  </div>
                  {examiner.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      <span className="truncate max-w-[100px]">{examiner.phone}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">
                    {dir === 'rtl' 
                      ? `${examiner.firstNameAr} ${examiner.lastNameAr}`
                      : `${examiner.firstNameEn} ${examiner.lastNameEn}`
                    }
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleAddExaminer(examiner)}
                  disabled={!!processingId}
                  className="flex-shrink-0 bg-green-50 hover:bg-green-100 text-green-600 hover:text-green-700"
                >
                  {processingId === examiner.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
            {filteredAvailableExaminers.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">
                {availableSearchQuery ? 
                  (dir === 'rtl' ? 'لا توجد نتائج للبحث' : 'No search results') :
                  t('dashboard.admin.organizations.members.noExaminersAvailable')
                }
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
} 